import { useState, useEffect } from 'react';
import { Download, Copy, Check, ChevronDown, ChevronRight, Key, Globe, Bell, ShoppingCart, CreditCard, FileText, TestTube, Rocket, Shield, Users, Terminal, AlertTriangle, Layers } from 'lucide-react';

const BASE_URL = 'https://caurapay.com/api';

// Codigos de respuesta del Debito Inmediato (C2P). Fuente: referencia oficial de
// Banco Plaza. La tabla completa se sirve en GET /public/debit/response-codes.
const DI_CODES = [
  { codigo: '0000', descripcion: 'Transaccion exitosa / enviada a BCV', accion: 'Pago aprobado. La orden pasa a paid y se emite el webhook order.paid' },
  { codigo: '0002', descripcion: 'Parametro obligatorio ausente o invalido', accion: 'Error en los datos de la peticion. Causa mas comun: el nombre del pagador supera los 35 caracteres permitidos' },
  { codigo: '0003', descripcion: 'Parametro no cumple formato', accion: 'Revisa cedula/RIF (12 caracteres), telefono (0XXX + numero) y cuenta (20 digitos)' },
  { codigo: '0016 / 0042 / AM04', descripcion: 'Saldo insuficiente', accion: 'El pagador no tiene fondos. Que reintente con otro monto o cuenta' },
  { codigo: '0031', descripcion: 'Pendiente de liquidacion del BCV', accion: 'Pago en proceso. Caura Pay lo confirma automaticamente y emite el webhook' },
  { codigo: '0032', descripcion: 'Excede cantidad de operaciones diarias', accion: 'Limite del banco del pagador. Reintentar al dia siguiente' },
  { codigo: '0033 / 0049', descripcion: 'Excede limite diario o configurado', accion: 'El pagador debe aumentar su limite de C2P en su banca en linea' },
  { codigo: '0035 / 0036', descripcion: 'Beneficiario no registrado por el pagador', accion: 'El pagador debe autorizar al comercio como beneficiario de debito inmediato' },
  { codigo: '0045', descripcion: 'Cuenta no permite debitos', accion: 'Cuenta del pagador restringida. Debe consultar con su banco' },
  { codigo: '0065 / 0075 / 0085 / 0095 / 100 / TKCM', descripcion: 'Clave SMS incorrecta o expirada', accion: 'Solicita un token nuevo (paso 1) y reintenta: el token vence en 2 minutos' },
  { codigo: '0096', descripcion: 'Servicio del banco no disponible', accion: 'Intermitencia del banco. Caura Pay reintenta automaticamente; vuelve a intentar en unos minutos' },
  { codigo: '109', descripcion: 'Identificacion y cuenta no afiliadas al cobro inmediato', accion: 'El pagador debe afiliar su cuenta al C2P en su banca en linea' },
  { codigo: '110', descripcion: 'El telefono no corresponde a la cedula del pagador', accion: 'El pagador debe usar el telefono registrado en su banco' },
  { codigo: '9999', descripcion: 'Enviada al BCV sin respuesta definitiva', accion: 'Queda pendiente; Caura Pay la verifica contra los movimientos reales' },
  { codigo: 'BE20', descripcion: 'La longitud del nombre supera el maximo permitido', accion: 'El nombre del pagador admite maximo 35 caracteres' },
  { codigo: 'RC08', descripcion: 'Codigo de banco inexistente en el sistema de compensacion', accion: 'El pagador debe elegir otro banco' },
  { codigo: 'RJCT', descripcion: 'Operacion rechazada por el BCV', accion: 'Reintentar o usar otro metodo de pago' },
  { codigo: 'A001 / A002 / A003 / A004', descripcion: 'Firma, autorizacion o API key del comercio ante el banco', accion: 'Error de integracion: contacta a soporte de Caura Pay' },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors" title="Copiar">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function CodeBlock({ code, lang = 'bash' }) {
  return (
    <div className="relative bg-[#1e1e2e] rounded-lg overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 px-4 pt-2">{lang}</div>
      <pre className="px-4 pb-3 pt-1 text-sm text-gray-200 overflow-x-auto whitespace-pre-wrap break-words"><code>{code}</code></pre>
      <CopyButton text={code} />
    </div>
  );
}

function EnvBadge({ env }) {
  if (env === 'qa') return <span data-testid="badge-qa" className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 border border-amber-300">QA / Test</span>;
  return <span data-testid="badge-prod" className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-full bg-green-100 text-green-800 border border-green-300">Produccion</span>;
}

function Section({ id, icon: Icon, title, children, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`section-${id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <span className="text-base font-semibold text-gray-800 flex-1">{title}</span>
        {badge && <span className="mr-2">{badge}</span>}
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function Endpoint({ method, path, desc, body, response, headers, env }) {
  const methodColors = { GET: 'bg-green-100 text-green-800', POST: 'bg-blue-100 text-blue-800', PUT: 'bg-yellow-100 text-yellow-800', DELETE: 'bg-red-100 text-red-800' };
  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${methodColors[method] || 'bg-gray-100'}`}>{method}</span>
        <code className="text-sm font-mono font-medium text-gray-800">{path}</code>
        {env && <EnvBadge env={env} />}
      </div>
      <p className="text-sm text-gray-600">{desc}</p>
      {headers && <div className="text-xs text-gray-500"><span className="font-medium">Headers:</span> {headers}</div>}
      {body && (<div><p className="text-xs font-medium text-gray-500 mb-1">Body:</p><CodeBlock code={JSON.stringify(body, null, 2)} lang="json" /></div>)}
      {response && (<div><p className="text-xs font-medium text-gray-500 mb-1">Respuesta:</p><CodeBlock code={JSON.stringify(response, null, 2)} lang="json" /></div>)}
    </div>
  );
}

// QA Test Merchants Data
const QA_MERCHANTS = [
  {
    company: "Tienda Online ABC",
    email: "admin@tiendaabc.com",
    password: "TestQA123!",
    rif: "J-40012345-6",
    cuenta: "01380010310100301001",
    telefono: "04121234567",
    project: "Tienda ABC - E-commerce",
  },
  {
    company: "Restaurant La Fogata",
    email: "admin@lafogata.com",
    password: "TestQA123!",
    rif: "J-30098765-4",
    cuenta: "01380010310100301002",
    telefono: "04149876543",
    project: "La Fogata - Delivery App",
  },
  {
    company: "Tech Solutions CA",
    email: "admin@techsolutions.com",
    password: "TestQA123!",
    rif: "J-50055667-8",
    cuenta: "01380010310100301003",
    telefono: "04165556677",
    project: "TechPay - SaaS Platform",
  },
];

const DOC_MARKDOWN = `# Caura Pay - Documentacion API v1

## Entornos

### QA (Pruebas)
- URL Base: ${BASE_URL}
- API Keys: Prefijo cpt_test_
- Proposito: Integrar y probar sin dinero real

### Produccion
- URL Base: ${BASE_URL}
- API Keys: Prefijo cpt_live_
- Proposito: Transacciones reales

## Autenticacion
Todas las peticiones requieren el header X-API-Key.
- QA: Usa tu API Key de prueba (cpt_test_...)
- Produccion: Usa tu API Key de produccion (cpt_live_...)

\`\`\`
X-API-Key: tu_api_key_aqui
Content-Type: application/json
\`\`\`

## Comercios QA de Prueba

| Comercio | Email | RIF | Cuenta para Dispersiones |
|----------|-------|-----|--------------------------|
| Tienda Online ABC | admin@tiendaabc.com | J-40012345-6 | 01380010310100301001 |
| Restaurant La Fogata | admin@lafogata.com | J-30098765-4 | 01380010310100301002 |
| Tech Solutions CA | admin@techsolutions.com | J-50055667-8 | 01380010310100301003 |

Password comun QA: TestQA123!

## Datos de Prueba para Pagadores (QA)

Usa estos datos mock al crear ordenes en el entorno QA:

| Campo | Valor de prueba |
|-------|----------------|
| payer_name | Juan Perez |
| payer_email | juan.perez@test.com |
| payer_document | V12345678 |
| payer_phone | 04141234567 |
| amount | 100.00 (VES) |

### Datos para verificar pago P2P (Pago Movil)
| Campo | Valor |
|-------|-------|
| Banco pagador | 0138 (Banco Plaza) |
| Telefono pagador (codigo) | 0414 |
| Telefono pagador (numero) | 1234567 |
| Referencia | Cualquier numero de 6-12 digitos |
| Fecha | Fecha actual (DD/MM/YYYY) |

### Datos para Transferencia BCV
| Campo | Valor |
|-------|-------|
| Banco origen | 0138 |
| Referencia | Numero de referencia de la transferencia |
| Fecha | Fecha actual (DD/MM/YYYY) |

---

## Endpoints

### 1. Crear Orden de Pago
\`\`\`
POST ${BASE_URL}/v1/orders
\`\`\`

**Body:**
\`\`\`json
{
  "amount": 500.00,
  "currency": "VES",
  "description": "Pago de servicio premium",
  "order_reference": "REF-001",
  "payer_name": "Juan Perez",
  "payer_email": "juan@email.com",
  "payer_document": "V12345678",
  "payer_phone": "04141234567",
  "success_redirect_url": "https://tu-sitio.com/pago-exitoso",
  "error_redirect_url": "https://tu-sitio.com/pago-fallido",
  "expires_in_minutes": 60
}
\`\`\`

**Campos requeridos:** amount, description
**Campos opcionales:** payer_name, payer_email, payer_document, payer_phone, order_reference, success_redirect_url, error_redirect_url, cancel_redirect_url, expires_in_minutes (default: 60, max: 1440), metadata

**Respuesta exitosa (201):**
\`\`\`json
{
  "url": "https://caurapay.com/pay/abc-123-def",
  "order_id": "abc-123-def",
  "code": "XYZ123"
}
\`\`\`

**Uso:** Redirige al usuario a la URL devuelta para que complete el pago.

### 2. Consultar Estado de Orden
\`\`\`
GET ${BASE_URL}/v1/orders/{order_id}
\`\`\`

**Respuesta:**
\`\`\`json
{
  "order_id": "abc-123-def",
  "order_code": "XYZ123",
  "amount": 500.00,
  "currency": "VES",
  "status": "paid",
  "payment_url": "https://caurapay.com/pay/abc-123-def",
  "payer_name": "Juan Perez",
  "payer_email": "juan@email.com",
  "created_at": "2026-03-31T10:00:00Z",
  "expires_at": "2026-03-31T10:30:00Z",
  "paid_at": "2026-03-31T10:15:00Z",
  "payment_method_used": "p2p",
  "payment_reference": "000691653554"
}
\`\`\`

**Estados posibles:** pending, paid, expired, cancelled, failed

### 3. Cancelar Orden
\`\`\`
POST ${BASE_URL}/v1/orders/{order_id}/cancel
\`\`\`

**Respuesta:**
\`\`\`json
{
  "message": "Orden cancelada",
  "order_id": "abc-123-def"
}
\`\`\`

### 4. Metodos de Pago Disponibles
\`\`\`
GET ${BASE_URL}/v1/payment-methods
\`\`\`

**Respuesta:**
\`\`\`json
{
  "environment": "test",
  "payment_methods": [
    {"code": "p2p", "name": "Pago Movil", "available": true},
    {"code": "c2p", "name": "Cobro a Persona", "available": true},
    {"code": "immediate_debit", "name": "Debito Inmediato", "available": true},
    {"code": "bcv_transfer", "name": "Transferencia BCV", "available": true}
  ]
}
\`\`\`

### 5. Info del Proyecto
\`\`\`
GET ${BASE_URL}/v1/project
\`\`\`

**Respuesta:**
\`\`\`json
{
  "project_id": "uuid-del-proyecto",
  "name": "Mi Proyecto",
  "status": "active",
  "environment": "test"
}
\`\`\`

### 6. Tasa de Cambio BCV
\`\`\`
GET ${BASE_URL}/v1/exchange-rate
\`\`\`

**Respuesta:**
\`\`\`json
{
  "source": "BCV",
  "usd_ves": 471.70,
  "eur_ves": 542.35,
  "rates": {"USD": 471.70, "EUR": 542.35},
  "fecha": "31/03/2026 00:39 UTC",
  "cached": false
}
\`\`\`

Fuente primaria: BCV directo. Fallback: ExchangeRate-API. Cache: 30 minutos.
La tasa tambien se muestra en la pasarela de pago como referencia en USD.

---

## Integracion Whitelabel (Cobro Directo)

Ademas del flujo hosted (redirigir al pagador a la payment_url de Caura Pay), puedes construir TU PROPIA pantalla de pago y confirmar la transaccion directamente via API, manteniendo tu marca de punta a punta.

Como funciona:
1. Tu backend crea la orden con POST /v1/orders (con tu API Key) y obtiene el order_id.
2. Tu frontend muestra tu propio formulario de pago.
3. Confirmas el pago con los endpoints publicos de abajo (usan el order_id, NO requieren API Key).

### Consultar datos de la orden
\`\`\`
GET ${BASE_URL}/public/orders/{order_id}
\`\`\`
Devuelve monto, moneda, estado y merchant_bank (RIF, banco y cuenta receptora, telefono) para mostrarle al pagador a que cuenta pagar.

### Pago Movil / C2P / Transferencia BCV
\`\`\`
POST ${BASE_URL}/public/orders/{order_id}/verify-payment
\`\`\`
Body:
\`\`\`json
{
  "payment_method": "p2p",
  "amount": 500.00,
  "reference": "575987",
  "date": "27/08/2025",
  "bankCode": "0102",
  "phoneCode": "0424",
  "phone": "1234567",
  "docType": "V",
  "docNumber": "12345678"
}
\`\`\`
- payment_method: p2p (Pago Movil), c2p (Cobro a Persona) o bcv_transfer (Transferencia).
- date en formato DD/MM/YYYY.
- phoneCode, phone, docType, docNumber solo aplican a p2p / c2p. Para bcv_transfer basta bankCode + reference + date.

Respuestas:
- 200 success:true -> pago verificado y orden pagada.
- 202 verification_in_progress -> el banco aun no refleja el pago (1-3 min). Se reintenta en background; espera el webhook order.paid o consulta GET /v1/orders/[order_id].
- 400 amount_mismatch -> el monto pagado no coincide (incluye expected_amount y received_amount).
- 400 error -> no se pudo verificar (incluye diagnostic).

### Debito Inmediato (token SMS)
\`\`\`
POST ${BASE_URL}/public/orders/{order_id}/debit/request-token
\`\`\`
Body (paso 1 - solicitar token, llega SMS al pagador):
\`\`\`json
{
  "identificacion": "V00012345678",
  "banco": "0138",
  "cuenta": "01380011130110248648",
  "telefono": "04241234567",
  "ip": "192.168.1.1"
}
\`\`\`
- identificacion: 12 caracteres (1 letra + 11 digitos). Envia cuenta O telefono (al menos uno).

\`\`\`
POST ${BASE_URL}/public/orders/{order_id}/debit/execute
\`\`\`
Body (paso 2 - ejecutar con token de 8 digitos, valido 2 min):
\`\`\`json
{ "token": "12345678", "ip": "192.168.1.1" }
\`\`\`
Respuestas: codigo 0000 = pagado; codigo 9999 = enviado al BCV (pending, se confirma por webhook); otro = error.

> Los endpoints /public/... se autentican solo con el order_id (que obtienes de forma privada al crear la orden). Crea la orden SIEMPRE desde tu backend con tu API Key; nunca la expongas en el frontend.

---

## Webhooks

Los webhooks permiten recibir notificaciones automaticas cuando cambia el estado de una orden.

### Seguridad: Firma HMAC-SHA256
Cada entrega incluye una firma digital para verificar autenticidad:
- El **secret** se genera automaticamente al crear el webhook
- La firma se envia en el header **X-Caura-Signature**
- Debes guardar el secret de forma segura y verificar la firma en cada entrega

### Crear Webhook
\`\`\`
POST ${BASE_URL}/v1/webhooks
\`\`\`

**Body:**
\`\`\`json
{
  "url": "https://tu-servidor.com/webhook/caura",
  "events": ["order.paid", "order.expired", "order.cancelled", "order.failed"]
}
\`\`\`

**Respuesta:**
\`\`\`json
{
  "webhook_id": "wh-123",
  "url": "https://tu-servidor.com/webhook/caura",
  "events": ["order.paid", "order.expired"],
  "secret": "whsec_abc123...",
  "active": true
}
\`\`\`

**IMPORTANTE:** Guarda el campo "secret" de forma segura. Lo necesitaras para verificar las firmas.

### Listar Webhooks
\`\`\`
GET ${BASE_URL}/v1/webhooks
\`\`\`

### Eliminar Webhook
\`\`\`
DELETE ${BASE_URL}/v1/webhooks/{webhook_id}
\`\`\`

### Ver Historial de Entregas
\`\`\`
GET ${BASE_URL}/v1/webhooks/{webhook_id}/deliveries
\`\`\`

### Formato de Entrega

Caura Pay envia un POST a tu URL con el siguiente formato:

**Headers:**
\`\`\`
Content-Type: application/json
X-Caura-Signature: hmac_sha256_del_payload_con_tu_secret
X-Caura-Event: order.paid
X-Caura-Delivery: delivery-id-unico
\`\`\`

**Body:**
\`\`\`json
{
  "event": "order.paid",
  "webhook_id": "wh-123",
  "delivery_id": "del-456",
  "timestamp": "2026-03-31T10:15:00Z",
  "data": {
    "order_id": "abc-123-def",
    "order_code": "XYZ123",
    "amount": 500.00,
    "currency": "VES",
    "payment_method": "p2p",
    "payment_reference": "000691653554",
    "paid_at": "2026-03-31T10:15:00Z"
  }
}
\`\`\`

### Verificar Firma (Python)
\`\`\`python
import hmac, hashlib, json

def verify_caura_signature(payload_dict, signature, secret):
    """Verifica la firma HMAC-SHA256 de un webhook de Caura Pay."""
    expected = hmac.new(
        secret.encode('utf-8'),
        json.dumps(payload_dict, sort_keys=True, default=str).encode('utf-8'),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)

# Uso en tu endpoint:
# signature = request.headers.get('X-Caura-Signature')
# payload = request.json()
# is_valid = verify_caura_signature(payload, signature, 'tu_webhook_secret')
\`\`\`

### Verificar Firma (Node.js)
\`\`\`javascript
const crypto = require('crypto');

function verifyCauraSignature(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload, Object.keys(payload).sort()))
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature), Buffer.from(expected)
  );
}
\`\`\`

### Reintentos
Si tu servidor no responde con codigo 2xx, Caura Pay reintenta hasta 3 veces:
- Intento 1: Inmediato
- Intento 2: 5 segundos despues
- Intento 3: 30 segundos despues

### Eventos Disponibles
| Evento | Descripcion |
|--------|------------|
| order.paid | Orden pagada exitosamente |
| order.expired | Orden expirada sin pago |
| order.cancelled | Orden cancelada |
| order.failed | Error en el procesamiento |

---

## Ejemplo Completo de Integracion

### cURL
\`\`\`bash
# 1. Crear orden
curl -X POST ${BASE_URL}/v1/orders \\
  -H "X-API-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"amount":500,"currency":"VES","description":"Pago servicio","order_reference":"REF-001","payer_name":"Juan","payer_email":"juan@test.com","payer_document":"V12345678","payer_phone":"04141234567"}'

# 2. Consultar orden
curl ${BASE_URL}/v1/orders/ORDER_ID -H "X-API-Key: tu_api_key"

# 3. Consultar tasa
curl ${BASE_URL}/v1/exchange-rate -H "X-API-Key: tu_api_key"

# 4. Cancelar orden
curl -X POST ${BASE_URL}/v1/orders/ORDER_ID/cancel -H "X-API-Key: tu_api_key"
\`\`\`

### JavaScript (Node.js)
\`\`\`javascript
const API_KEY = 'tu_api_key';
const BASE = '${BASE_URL}';

// Crear orden de pago
const res = await fetch(BASE + '/v1/orders', {
  method: 'POST',
  headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    amount: 500, currency: 'VES',
    description: 'Pago servicio', order_reference: 'REF-001',
    payer_name: 'Juan', payer_email: 'juan@test.com',
    payer_document: 'V12345678', payer_phone: '04141234567',
    success_redirect_url: 'https://tu-sitio.com/exito'
  })
});
const { url, order_id } = await res.json();
// Redirigir: window.location.href = url;

// Consultar estado
const order = await fetch(BASE + '/v1/orders/' + order_id, {
  headers: { 'X-API-Key': API_KEY }
}).then(r => r.json());

// Consultar tasa de cambio
const rate = await fetch(BASE + '/v1/exchange-rate', {
  headers: { 'X-API-Key': API_KEY }
}).then(r => r.json());
console.log('Tasa USD/VES:', rate.usd_ves);
\`\`\`

### Python
\`\`\`python
import requests

API_KEY = 'tu_api_key'
BASE = '${BASE_URL}'
HEADERS = {'X-API-Key': API_KEY}

# Crear orden
resp = requests.post(f'{BASE}/v1/orders', json={
    'amount': 500, 'currency': 'VES',
    'description': 'Pago servicio', 'order_reference': 'REF-001',
    'payer_name': 'Juan', 'payer_email': 'juan@test.com',
    'payer_document': 'V12345678', 'payer_phone': '04141234567'
}, headers=HEADERS)
data = resp.json()
payment_url = data['url']

# Consultar estado
order = requests.get(f'{BASE}/v1/orders/{data["order_id"]}', headers=HEADERS).json()

# Consultar tasa BCV
rate = requests.get(f'{BASE}/v1/exchange-rate', headers=HEADERS).json()
print(f'Tasa USD/VES: {rate["usd_ves"]}')
\`\`\`

---

## Flujo de Integracion

1. Tu servidor crea una orden via API con los datos del pagador
2. Caura Pay devuelve una URL de pago unica
3. Rediriges al pagador a esa URL
4. El pagador elige metodo de pago (Pago Movil, Transferencia BCV, Debito o C2P)
5. Caura Pay verifica el pago automaticamente
6. Si se configuro webhook, se notifica a tu servidor (con firma HMAC-SHA256)
7. El pagador es redirigido a tu success_redirect_url o error_redirect_url

---

## Soporte
Email: soporte@caurapay.com
`;

export default function Documentation() {
  const [activeEnv, setActiveEnv] = useState('qa');
  const [activeKey, setActiveKey] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/merchant/api-keys`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(keys => { if (keys.length > 0) setActiveKey(keys[0].key_prefix || 'tu_api_key'); }).catch(() => {});
    }
  }, []);

  const downloadDoc = (format) => {
    const content = DOC_MARKDOWN;
    const blob = new Blob([content], { type: format === 'md' ? 'text/markdown' : 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `caura-pay-api-docs.${format}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exampleKey = activeEnv === 'qa' ? 'cpt_test_xxxxxxxxxx...' : (activeKey || 'cpt_live_xxxxxxxxxx...');

  return (
    <div data-testid="documentation-page" className="min-h-screen bg-gray-50">
      {/* Top nav bar */}
      <div className="bg-[#051C2C] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Caura Pay API</h1>
              <p className="text-xs text-blue-300">Documentacion de Integracion v1</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="download-md-btn" onClick={() => downloadDoc('md')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/20 rounded-lg hover:bg-white/10 transition-colors">
              <Download className="w-3.5 h-3.5" /> .md
            </button>
            <button data-testid="download-txt-btn" onClick={() => downloadDoc('txt')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/20 rounded-lg hover:bg-white/10 transition-colors">
              <Download className="w-3.5 h-3.5" /> .txt
            </button>
          </div>
        </div>
      </div>

      {/* Environment Switcher */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Entorno:</span>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              data-testid="env-switch-qa"
              onClick={() => setActiveEnv('qa')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeEnv === 'qa' ? 'bg-amber-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <TestTube className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              QA / Test
            </button>
            <button
              data-testid="env-switch-prod"
              onClick={() => setActiveEnv('prod')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeEnv === 'prod' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Rocket className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Produccion
            </button>
          </div>
          <div className="ml-auto text-xs text-gray-400">
            URL Base: <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700">{BASE_URL}</code>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Environment Info Banner */}
        {activeEnv === 'qa' ? (
          <div data-testid="qa-banner" className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <TestTube className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-base font-bold text-amber-900">Entorno QA / Pruebas</h2>
                <p className="text-sm text-amber-700 mt-1">Usa API Keys con prefijo <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs font-mono font-bold">cpt_test_</code> para probar la integracion sin dinero real.</p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-amber-800">
                  <div><span className="font-semibold">URL Base:</span> {BASE_URL}</div>
                  <div><span className="font-semibold">Transacciones:</span> Simuladas / Sin costo</div>
                  <div><span className="font-semibold">RIF por comercio:</span> Individual (ver tabla abajo)</div>
                  <div><span className="font-semibold">Credenciales:</span> Provistas por Super Admin</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="prod-banner" className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Rocket className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-base font-bold text-green-900">Entorno de Produccion</h2>
                <p className="text-sm text-green-700 mt-1">Usa API Keys con prefijo <code className="bg-green-100 px-1.5 py-0.5 rounded text-xs font-mono font-bold">cpt_live_</code> para transacciones reales.</p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-green-800">
                  <div><span className="font-semibold">URL Base:</span> {BASE_URL}</div>
                  <div><span className="font-semibold">Transacciones:</span> Reales / Con costo</div>
                  <div><span className="font-semibold">RIF:</span> El registrado en tu cuenta Caura Pay</div>
                  <div><span className="font-semibold">Requiere:</span> Cuenta activa + API Key de produccion</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Start */}
        <div className="bg-gradient-to-br from-[#051C2C] to-[#003594] rounded-xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Terminal className="w-5 h-5 text-blue-300" />
            <h2 className="text-lg font-bold">Inicio Rapido</h2>
            <EnvBadge env={activeEnv} />
          </div>
          <p className="text-sm text-blue-200 mb-4">Todas las peticiones requieren tu API Key en el header:</p>
          <CodeBlock code={`curl -X POST ${BASE_URL}/v1/orders \\\n  -H "X-API-Key: ${exampleKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":500,"currency":"VES","description":"Pago de prueba","order_reference":"REF-001","payer_name":"Juan Perez","payer_email":"juan@test.com","payer_document":"V12345678","payer_phone":"04141234567"}'`} lang="bash" />
          <div className="mt-3 flex items-center gap-4 text-xs text-blue-300">
            <span>URL Base: <code className="bg-white/10 px-2 py-0.5 rounded">{BASE_URL}</code></span>
            <span>API Key: <code className="bg-white/10 px-2 py-0.5 rounded">{activeEnv === 'qa' ? 'cpt_test_...' : 'cpt_live_...'}</code></span>
          </div>
        </div>

        {/* QA Merchants Table */}
        {activeEnv === 'qa' && (
          <Section id="qa-merchants" icon={Users} title="Comercios QA de Prueba" defaultOpen={true} badge={<EnvBadge env="qa" />}>
            <p className="text-sm text-gray-600 mt-3 mb-3">Estos comercios estan pre-configurados con credenciales individuales para el entorno QA de Caura Pay.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="qa-merchants-table">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Comercio</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Email / Password</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">RIF</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Cuenta para Dispersiones</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Telefono</th>
                  </tr>
                </thead>
                <tbody>
                  {QA_MERCHANTS.map((m, i) => (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-gray-900">{m.company}</div>
                        <div className="text-xs text-gray-500">{m.project}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{m.email}</code>
                        <div className="text-xs text-gray-500 mt-0.5">{m.password}</div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{m.rif}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{m.cuenta}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{m.telefono}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  <span className="font-semibold">Nota:</span> Cada comercio registra su RIF y cuenta bancaria para que Caura Pay pueda dispersar los fondos. Las API Keys de tipo <code className="font-mono bg-amber-100 px-1 rounded">cpt_test_</code> operan en el entorno QA de Caura Pay. Inicia sesion con las credenciales de cualquier comercio para obtener su API Key desde el panel.
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Production Requirements */}
        {activeEnv === 'prod' && (
          <Section id="prod-requirements" icon={Shield} title="Requisitos de Produccion" defaultOpen={true} badge={<EnvBadge env="prod" />}>
            <div className="mt-3 space-y-3">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-semibold text-red-800 mb-2">Antes de usar Produccion</h4>
                <ul className="text-xs text-red-700 space-y-1.5">
                  <li className="flex items-start gap-2"><span className="font-bold">1.</span> Tener tu cuenta de comercio activa en Caura Pay</li>
                  <li className="flex items-start gap-2"><span className="font-bold">2.</span> Solicitar la activacion del entorno de produccion al equipo de Caura Pay</li>
                  <li className="flex items-start gap-2"><span className="font-bold">3.</span> Generar API Keys de produccion (cpt_live_) desde tu panel</li>
                  <li className="flex items-start gap-2"><span className="font-bold">4.</span> Haber probado exitosamente en el entorno QA</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Diferencias clave con QA</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="font-medium text-gray-700">QA</div>
                    <div className="text-gray-500">URL: {BASE_URL}</div>
                    <div className="text-gray-500">API Keys: cpt_test_...</div>
                    <div className="text-gray-500">Dinero: Simulado</div>
                  </div>
                  <div className="space-y-1">
                    <div className="font-medium text-gray-700">Produccion</div>
                    <div className="text-gray-500">URL: {BASE_URL}</div>
                    <div className="text-gray-500">API Keys: cpt_live_...</div>
                    <div className="text-gray-500">Dinero: Real</div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Auth */}
        <Section id="auth" icon={Key} title="Autenticacion" defaultOpen={true}>
          <p className="text-sm text-gray-600 mt-3">Incluye tu API Key en cada peticion con el header <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key</code>.</p>
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            <span className="font-semibold">Tipo de Key:</span> {activeEnv === 'qa'
              ? <> Usa <code className="font-mono bg-blue-100 px-1 rounded">cpt_test_...</code> para QA. Obtienla desde el panel del comercio o al ejecutar el seed.</>
              : <> Usa <code className="font-mono bg-blue-100 px-1 rounded">cpt_live_...</code> para produccion. Generala desde el panel del comercio.</>}
          </div>
          <CodeBlock code={`X-API-Key: ${exampleKey}\nContent-Type: application/json`} lang="http" />
        </Section>

        {/* Orders */}
        <Section id="orders" icon={ShoppingCart} title="Ordenes de Pago" defaultOpen={true}>
          <Endpoint method="POST" path="/v1/orders" desc="Crear una nueva orden de pago. Devuelve la URL donde redirigir al pagador."
            headers={`X-API-Key: ${exampleKey}`}
            body={{ amount: 500.00, currency: "VES", description: "Pago de servicio", order_reference: "REF-001", payer_name: "Juan Perez", payer_email: "juan@email.com", payer_document: "V12345678", payer_phone: "04141234567", success_redirect_url: "https://tu-sitio.com/exito", error_redirect_url: "https://tu-sitio.com/error", expires_in_minutes: 60 }}
            response={{ url: "https://caurapay.com/pay/abc-123", order_id: "abc-123", code: "XYZ123" }} />
          <Endpoint method="GET" path="/v1/orders/{order_id}" desc="Consultar el estado de una orden. Estados: pending, paid, expired, cancelled, failed."
            response={{ order_id: "abc-123", status: "paid", amount: 500.00, currency: "VES", payment_method_used: "p2p", paid_at: "2026-03-24T10:15:00Z", environment: activeEnv === 'qa' ? 'test' : 'live' }} />
          <Endpoint method="POST" path="/v1/orders/{order_id}/cancel" desc="Cancelar una orden pendiente."
            response={{ message: "Orden cancelada", order_id: "abc-123" }} />
          <Endpoint method="GET" path="/v1/payment-methods" desc="Listar metodos de pago disponibles para el entorno actual."
            response={{ environment: activeEnv === 'qa' ? 'test' : 'live', payment_methods: [{ code: "p2p", name: "Pago Movil", available: true }, { code: "c2p", name: "Cobro a Persona", available: true }, { code: "immediate_debit", name: "Debito Inmediato", available: activeEnv === 'qa' }, { code: "bcv_transfer", name: "Transferencia BCV", available: true }] }} />
          <Endpoint method="GET" path="/v1/project" desc="Informacion del proyecto asociado a la API Key."
            response={{ project_id: "uuid-del-proyecto", name: "Mi Proyecto", status: "active", environment: activeEnv === 'qa' ? 'test' : 'live' }} />
          <Endpoint method="GET" path="/v1/exchange-rate" desc="Tasa de cambio BCV actual (USD/VES, EUR/VES). Cache de 30 min. Fuente: BCV directo con fallback a ExchangeRate-API."
            response={{ source: "BCV", usd_ves: 471.70, eur_ves: 542.35, fecha: "31/03/2026 00:39 UTC", cached: false }} />
        </Section>

        {/* Whitelabel */}
        <Section id="whitelabel" icon={Layers} title="Integracion Whitelabel (Cobro Directo)" defaultOpen={true}>
          <div className="mt-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900">
            <p className="font-semibold mb-1">Dos formas de integrar</p>
            <p className="text-xs text-indigo-800 leading-relaxed">
              <strong>Hosted:</strong> creas la orden y rediriges al pagador a la <code className="bg-indigo-100 px-1 rounded">payment_url</code> de Caura Pay (mas rapido).
              <br />
              <strong>Whitelabel:</strong> muestras TU PROPIO formulario de pago y confirmas la transaccion con los endpoints publicos de abajo, manteniendo tu marca de punta a punta.
            </p>
          </div>
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <span className="font-semibold">Flujo:</span> 1) Tu backend crea la orden con <code className="bg-amber-100 px-1 rounded">POST /v1/orders</code> (con tu API Key) y obtiene el <code className="bg-amber-100 px-1 rounded">order_id</code>. 2) Tu frontend usa ese <code className="bg-amber-100 px-1 rounded">order_id</code> para llamar los endpoints <code className="bg-amber-100 px-1 rounded">/public/...</code> (no requieren API Key).
          </div>

          <h4 className="text-sm font-semibold text-gray-800 mt-5">Consultar la orden</h4>
          <Endpoint method="GET" path="/public/orders/{order_id}" desc="Datos de la orden + datos bancarios del comercio (merchant_bank) para mostrarle al pagador a que cuenta pagar. No requiere API Key."
            response={{ order_id: "abc-123", amount: 500.00, currency: "VES", status: "pending", merchant_bank: { company_name: "Mi Tienda", rif: "J-40012345-6", banco_receptor: "0138", cuenta_receptora: "01380010310100301001", telefono_cobrador: "04241234567" } }} />

          <h4 className="text-sm font-semibold text-gray-800 mt-6">A) Pago Movil / C2P / Transferencia BCV</h4>
          <p className="text-xs text-gray-600 mt-1">El cliente paga desde su banco; luego envias los datos de la transaccion para verificar contra Banco Plaza.</p>
          <Endpoint method="POST" path="/public/orders/{order_id}/verify-payment" desc="Verifica un pago Pago Movil (p2p), Cobro a Persona (c2p) o Transferencia (bcv_transfer). date en formato DD/MM/YYYY. phoneCode/phone/docType/docNumber solo aplican a p2p y c2p; para bcv_transfer basta bankCode + reference + date."
            body={{ payment_method: "p2p", amount: 500.00, reference: "575987", date: "27/08/2025", bankCode: "0102", phoneCode: "0424", phone: "1234567", docType: "V", docNumber: "12345678" }}
            response={{ success: true, order_id: "abc-123", payment_reference: "0102-575987-2025-08-27", message: "Pago verificado y procesado exitosamente" }} />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-100"><th className="px-3 py-2 text-left font-semibold text-gray-700">Respuesta</th><th className="px-3 py-2 text-left font-semibold text-gray-700">Significado</th></tr></thead>
              <tbody>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">200 success:true</td><td className="px-3 py-2 text-xs text-gray-600">Pago verificado y orden marcada como pagada</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">202 verification_in_progress</td><td className="px-3 py-2 text-xs text-gray-600">El banco aun no refleja el pago (latencia 1-3 min). Se reintenta en background; espera el webhook order.paid o consulta GET /v1/orders/[order_id]</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">400 amount_mismatch</td><td className="px-3 py-2 text-xs text-gray-600">El monto pagado no coincide (incluye expected_amount y received_amount)</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">400 error</td><td className="px-3 py-2 text-xs text-gray-600">No se pudo verificar (incluye diagnostic)</td></tr>
              </tbody>
            </table>
          </div>

          <h4 className="text-sm font-semibold text-gray-800 mt-6">B) Debito Inmediato (token SMS)</h4>
          <Endpoint method="POST" path="/public/orders/{order_id}/debit/request-token" desc="Paso 1: solicita el token. Banco Plaza envia un SMS de 8 digitos al pagador. Envia cuenta O telefono (al menos uno). identificacion = 12 caracteres (se acepta V-12345678 y se normaliza)."
            body={{ identificacion: "V00012345678", banco: "0138", cuenta: "01380011130110248648", telefono: "04241234567", ip: "192.168.1.1" }}
            response={{ success: true, codigo: "0000", mensaje: "Token enviado por SMS", tiempo_expiracion: 120 }} />
          <Endpoint method="POST" path="/public/orders/{order_id}/debit/execute" desc="Paso 2: ejecuta el cobro con el token de 8 digitos (valido 2 min). codigo 0000 = pagado; codigo 9999 = enviado al BCV (pending, se confirma luego por webhook); otro = error."
            body={{ token: "12345678", ip: "192.168.1.1" }}
            response={{ success: true, codigo: "0000", mensaje: "Pago procesado exitosamente", endtoend: "E2E...", referencia: "12345678" }} />

          <div className="mt-6" data-testid="di-response-codes-block">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Codigos de respuesta del Debito Inmediato</h4>
            <p className="text-xs text-gray-600 mb-2">
              Los endpoints <code className="bg-gray-100 px-1 rounded">debit/request-token</code> y{' '}
              <code className="bg-gray-100 px-1 rounded">debit/execute</code> devuelven el <code className="bg-gray-100 px-1 rounded">codigo</code> del banco,
              un <code className="bg-gray-100 px-1 rounded">mensaje</code> accionable para mostrar al pagador, la <code className="bg-gray-100 px-1 rounded">categoria</code> (quien
              debe resolverlo) y <code className="bg-gray-100 px-1 rounded">reintentable</code>. La tabla completa y siempre actualizada esta en{' '}
              <code className="bg-gray-100 px-1 rounded">GET {BASE_URL}/public/debit/response-codes</code>. El historial por orden
              (request enviado + respuesta literal del banco) esta en <code className="bg-gray-100 px-1 rounded">GET /v1/orders/{'{order_id}'}/debit-attempts</code>.
            </p>
            <div className="p-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              <span className="font-semibold">Limite importante:</span> el banco acepta como maximo <strong>35 caracteres alfanumericos</strong> en
              el nombre del pagador (<code className="bg-amber-100 px-1 rounded">payer_name</code> de la orden). Caura Pay lo normaliza y recorta
              automaticamente; si envias razones sociales muy largas, el nombre que vera el banco sera el recorte a 35 caracteres.
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-700">Codigo</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-700">Significado</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-700">Que hacer</th>
                  </tr>
                </thead>
                <tbody>
                  {DI_CODES.map((c) => (
                    <tr key={c.codigo} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{c.codigo}</td>
                      <td className="px-3 py-2 text-xs text-gray-800">{c.descripcion}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{c.accion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Ejemplo whitelabel (JavaScript)</h4>
            <CodeBlock code={`const BASE = '${BASE_URL}';\n\n// 1) (Backend) crear orden con tu API Key -> obtienes order_id\n// const { order_id } = await createOrderOnYourServer();\n\n// 2) (Frontend) verificar Pago Movil con tu propio formulario\nawait fetch(BASE + '/public/orders/' + order_id + '/verify-payment', {\n  method: 'POST',\n  headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify({\n    payment_method: 'p2p', amount: 500, reference: '575987',\n    date: '27/08/2025', bankCode: '0102',\n    phoneCode: '0424', phone: '1234567', docType: 'V', docNumber: '12345678'\n  })\n}).then(r => r.json());\n\n// Debito Inmediato:\n// a) solicitar token (SMS al pagador)\nawait fetch(BASE + '/public/orders/' + order_id + '/debit/request-token', {\n  method: 'POST', headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify({ identificacion: 'V00012345678', banco: '0138', telefono: '04241234567' })\n});\n// b) ejecutar con el token recibido por SMS (8 digitos)\nawait fetch(BASE + '/public/orders/' + order_id + '/debit/execute', {\n  method: 'POST', headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify({ token: '12345678' })\n}).then(r => r.json());`} lang="javascript" />
          </div>

          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
            <span className="font-semibold">Nota de seguridad:</span> los endpoints <code className="bg-gray-100 px-1 rounded">/public/...</code> se autentican solo con el <code className="bg-gray-100 px-1 rounded">order_id</code> (que obtienes de forma privada al crear la orden). Crea la orden SIEMPRE desde tu backend con tu API Key; nunca la expongas en el frontend.
          </div>
        </Section>

        {/* Webhooks */}
        <Section id="webhooks" icon={Bell} title="Webhooks">
          <p className="text-sm text-gray-600 mt-3 mb-2">Recibe notificaciones automaticas en tu servidor cuando cambie el estado de una orden.</p>
          
          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="text-sm font-semibold text-red-800 mb-1">Seguridad: Firma HMAC-SHA256</h4>
            <p className="text-xs text-red-700">Cada entrega incluye una firma digital en el header <code className="bg-red-100 px-1 rounded">X-Caura-Signature</code>. El <code className="bg-red-100 px-1 rounded">secret</code> se genera al crear el webhook. Guarda el secret de forma segura y verifica la firma en cada entrega para asegurar que proviene de Caura Pay.</p>
          </div>

          <Endpoint method="POST" path="/v1/webhooks" desc="Registrar un webhook. El secret se genera automaticamente."
            body={{ url: "https://tu-servidor.com/webhook/caura", events: ["order.paid", "order.expired", "order.cancelled", "order.failed"] }}
            response={{ webhook_id: "wh-123", url: "https://tu-servidor.com/webhook/caura", events: ["order.paid", "order.expired"], secret: "whsec_abc123... (GUARDAR DE FORMA SEGURA)", active: true }} />
          <Endpoint method="GET" path="/v1/webhooks" desc="Listar webhooks configurados." />
          <Endpoint method="DELETE" path="/v1/webhooks/{webhook_id}" desc="Eliminar un webhook." />
          <Endpoint method="GET" path="/v1/webhooks/{webhook_id}/deliveries" desc="Ver historial de entregas de un webhook." />

          <div className="mt-4 p-4 bg-blue-50 rounded-lg space-y-3">
            <h4 className="text-sm font-semibold text-blue-900">Formato de entrega del webhook</h4>
            <p className="text-xs text-blue-800">Caura Pay envia un POST a tu URL con estos headers:</p>
            <CodeBlock code={`Content-Type: application/json\nX-Caura-Signature: hmac_sha256_del_payload_con_tu_secret\nX-Caura-Event: order.paid\nX-Caura-Delivery: delivery-id-unico`} lang="http" />
            <p className="text-xs text-blue-800 mt-2">Body del webhook:</p>
            <CodeBlock code={JSON.stringify({ event: "order.paid", webhook_id: "wh-123", delivery_id: "del-456", timestamp: "2026-03-31T10:15:00Z", data: { order_id: "abc-123", order_code: "XYZ123", amount: 500.00, currency: "VES", payment_method: "p2p", payment_reference: "000691653554", paid_at: "2026-03-31T10:15:00Z" } }, null, 2)} lang="json" />
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">Verificar firma</h4>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Python</p>
              <CodeBlock code={`import hmac, hashlib, json\n\ndef verify_caura_signature(payload_dict, signature, secret):\n    expected = hmac.new(\n        secret.encode('utf-8'),\n        json.dumps(payload_dict, sort_keys=True, default=str).encode('utf-8'),\n        hashlib.sha256\n    ).hexdigest()\n    return hmac.compare_digest(signature, expected)\n\n# Uso:\n# signature = request.headers.get('X-Caura-Signature')\n# is_valid = verify_caura_signature(request.json(), signature, 'tu_secret')`} lang="python" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Node.js</p>
              <CodeBlock code={`const crypto = require('crypto');\n\nfunction verifyCauraSignature(payload, signature, secret) {\n  const expected = crypto\n    .createHmac('sha256', secret)\n    .update(JSON.stringify(payload, Object.keys(payload).sort()))\n    .digest('hex');\n  return crypto.timingSafeEqual(\n    Buffer.from(signature), Buffer.from(expected)\n  );\n}\n\n// Uso:\n// const sig = req.headers['x-caura-signature'];\n// const valid = verifyCauraSignature(req.body, sig, 'tu_secret');`} lang="javascript" />
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800"><span className="font-semibold">Reintentos:</span> Si tu servidor no responde con 2xx, Caura Pay reintenta 3 veces (inmediato, 5s, 30s).</p>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-100"><th className="px-3 py-2 text-left font-semibold text-gray-700">Evento</th><th className="px-3 py-2 text-left font-semibold text-gray-700">Descripcion</th></tr></thead>
              <tbody>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">order.paid</td><td className="px-3 py-2 text-xs text-gray-600">Orden pagada exitosamente</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">order.expired</td><td className="px-3 py-2 text-xs text-gray-600">Orden expirada sin pago</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">order.cancelled</td><td className="px-3 py-2 text-xs text-gray-600">Orden cancelada</td></tr>
                <tr className="border-t"><td className="px-3 py-2 font-mono text-xs">order.failed</td><td className="px-3 py-2 text-xs text-gray-600">Error en el procesamiento</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Integration Examples */}
        <Section id="examples" icon={FileText} title="Ejemplos de Integracion">
          <div className="mt-3 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">JavaScript / Node.js</h4>
              <CodeBlock code={`const API_KEY = '${exampleKey}';\nconst BASE = '${BASE_URL}';\n\n// Crear orden de pago\nconst res = await fetch(BASE + '/v1/orders', {\n  method: 'POST',\n  headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },\n  body: JSON.stringify({\n    amount: 500, currency: 'VES',\n    description: 'Pago servicio', order_reference: 'REF-001',\n    payer_name: 'Juan', payer_email: 'juan@test.com',\n    payer_document: 'V12345678', payer_phone: '04141234567',\n    success_redirect_url: 'https://tu-sitio.com/exito'\n  })\n});\nconst { url, order_id } = await res.json();\n// Redirigir: window.location.href = url;\n\n// Consultar estado\nconst order = await fetch(BASE + '/v1/orders/' + order_id, {\n  headers: { 'X-API-Key': API_KEY }\n}).then(r => r.json());\n\n// Consultar tasa de cambio BCV\nconst rate = await fetch(BASE + '/v1/exchange-rate', {\n  headers: { 'X-API-Key': API_KEY }\n}).then(r => r.json());\nconsole.log('USD/VES:', rate.usd_ves);`} lang="javascript" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Python</h4>
              <CodeBlock code={`import requests\n\nAPI_KEY = '${exampleKey}'\nBASE = '${BASE_URL}'\nHEADERS = {'X-API-Key': API_KEY}\n\n# Crear orden\nresp = requests.post(f'{BASE}/v1/orders', json={\n    'amount': 500, 'currency': 'VES',\n    'description': 'Pago servicio', 'order_reference': 'REF-001',\n    'payer_name': 'Juan', 'payer_email': 'juan@test.com',\n    'payer_document': 'V12345678', 'payer_phone': '04141234567'\n}, headers=HEADERS)\ndata = resp.json()\npayment_url = data['url']\n\n# Consultar estado\norder = requests.get(f'{BASE}/v1/orders/{data[\"order_id\"]}', headers=HEADERS).json()\n\n# Consultar tasa BCV\nrate = requests.get(f'{BASE}/v1/exchange-rate', headers=HEADERS).json()\nprint(f'Tasa USD/VES: {rate[\"usd_ves\"]}')`} lang="python" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">cURL</h4>
              <CodeBlock code={`# Crear orden\ncurl -X POST ${BASE_URL}/v1/orders \\\n  -H "X-API-Key: ${exampleKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"amount":500,"currency":"VES","description":"Pago","order_reference":"REF-001","payer_name":"Juan","payer_email":"juan@test.com","payer_document":"V12345678","payer_phone":"04141234567"}'\n\n# Consultar estado\ncurl ${BASE_URL}/v1/orders/ORDER_ID \\\n  -H "X-API-Key: ${exampleKey}"\n\n# Consultar tasa BCV\ncurl ${BASE_URL}/v1/exchange-rate \\\n  -H "X-API-Key: ${exampleKey}"\n\n# Cancelar orden\ncurl -X POST ${BASE_URL}/v1/orders/ORDER_ID/cancel \\\n  -H "X-API-Key: ${exampleKey}"`} lang="bash" />
            </div>
          </div>
        </Section>

        {/* Payment Flow */}
        <Section id="flow" icon={CreditCard} title="Flujo de Pago">
          <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-800 mb-3">
            <span className="font-semibold">Nota:</span> los pasos 2, 3 y 6 aplican al flujo <strong>hosted</strong>. En modo <strong>whitelabel</strong> muestras tu propio formulario y confirmas con los endpoints <code className="bg-indigo-100 px-1 rounded">/public/...</code> (ver seccion "Integracion Whitelabel").
          </div>
          <div className="mt-3 space-y-3">
            {[
              { step: 1, text: 'Tu servidor crea una orden via API con los datos del pagador', detail: `Usa tu API Key ${activeEnv === 'qa' ? 'de prueba (cpt_test_)' : 'de produccion (cpt_live_)'}` },
              { step: 2, text: 'Caura Pay devuelve una URL de pago unica', detail: 'Redirige al pagador a esta URL' },
              { step: 3, text: 'El pagador elige metodo de pago', detail: 'Pago Movil, Transferencia BCV, Debito Inmediato o C2P' },
              { step: 4, text: 'Caura Pay verifica el pago automaticamente', detail: 'Verificacion interna contra el sistema bancario' },
              { step: 5, text: 'Se notifica a tu servidor via webhook (si esta configurado)', detail: 'Evento order.paid con datos del pago' },
              { step: 6, text: 'El pagador es redirigido a tu success_redirect_url o error_redirect_url', detail: 'Flujo completado' },
            ].map(({ step, text, detail }) => (
              <div key={step} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#003594' }}>{step}</span>
                <div>
                  <p className="text-sm text-gray-700 font-medium">{text}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Download Reminder */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Descarga esta documentacion para tu equipo de desarrollo</p>
            <p className="text-xs text-gray-500 mt-0.5">Formato Markdown compatible con cualquier agente AI o desarrollador</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => downloadDoc('md')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#003594' }}>
              <Download className="w-4 h-4" /> Descargar .md
            </button>
            <button onClick={() => downloadDoc('txt')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
              <Download className="w-4 h-4" /> Descargar .txt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
